import type { HeroPlugin, HeroThemeTokens, ResolvedHeroPlugin } from './contract';
import { neutralPlugin, neutralItemIcon } from './neutral';
import { createSkinPlugin } from './skin';
import { pickModuleExports, keyBy } from './registry';
import { SKIN_ARCHETYPES } from '../content/heroes/assets';

/**
 * The hero PLUGIN + THEME registry (ADR-0099 / ADR-0100 / ADR-0110 §2 / ADR-0116) — AUTO-DISCOVERED. A hero is
 * registered TWO ways, both zero-list:
 *   1. SKIN heroes (the rule) ship only `art/` + `theme.ts` in `content/heroes/<hero>/` — no render code. The
 *      shared {@link createSkinPlugin} is built for each archetype with a base figure ({@link SKIN_ARCHETYPES}).
 *   2. BESPOKE heroes (the exception — a vector body, layered cosmetics, a hand-drawn scene) author their own
 *      `skin.tsx` exporting a {@link HeroPlugin}; this module GLOBS them and PREFERS them over the skin default.
 * Either way there is NO hand-maintained list: adding a hero is dropping its directory. A hero with neither art
 * nor a `skin.tsx` resolves to the NEUTRAL plugin (graceful degradation). Web-only (the
 * `@kidshome/hero-content/art` alias runs through Vite), like the `content/heroes/assets.ts` raster glob.
 */
const pluginModules = import.meta.glob('../content/heroes/*/skin.tsx', {
  eager: true,
}) as Record<string, Record<string, unknown>>;
const themeModules = import.meta.glob('../content/heroes/*/theme.ts', {
  eager: true,
}) as Record<string, Record<string, unknown>>;

/** The archetype slug is the hero's directory name: `../content/heroes/<archetype>/skin.tsx`. */
function archetypeOfPath(path: string): string {
  return path.split('/').at(-2) ?? '';
}

/** The HeroPlugin shape a `skin.tsx` exports — the value carrying `archetype` + `renderFigure`. */
const isHeroPlugin = (v: unknown): v is HeroPlugin =>
  !!v && typeof v === 'object' && 'archetype' in v && 'renderFigure' in v;

/**
 * Resolve a bespoke plugin into a fully-populated one (ADR-0102): fill any OMITTED render method over a base
 * (avatar = the figure at that form, scene = none, itemIcon = the neutral tinted tile), so a hero can author
 * only `renderFigure` and still render everywhere. A plain spread — not a Proxy — done ONCE at module load.
 */
function resolveHeroPlugin(plugin: HeroPlugin): ResolvedHeroPlugin {
  return {
    ...plugin,
    renderAvatar:
      plugin.renderAvatar ??
      (({ stageIndex, look, uid }) => plugin.renderFigure({ stageIndex, look, uid })),
    renderScene: plugin.renderScene ?? (() => null),
    renderItemIcon:
      plugin.renderItemIcon ?? (({ slug, className }) => neutralItemIcon(slug, className)),
  };
}

/** The bespoke plugins a hero authored by hand (the exception) — keyed by archetype, preferred over a default. */
const BESPOKE: Readonly<Record<string, ResolvedHeroPlugin>> = keyBy(
  pickModuleExports(pluginModules, isHeroPlugin).map(resolveHeroPlugin),
  (p) => p.archetype,
);

/**
 * The full registry: a shared skin renderer for every archetype with painted art, OVERRIDDEN by any bespoke
 * plugin of the same archetype (a hero that drops both art and a `skin.tsx` gets its hand-authored render).
 */
const PLUGINS: Readonly<Record<string, ResolvedHeroPlugin>> = {
  ...keyBy(
    SKIN_ARCHETYPES.map((archetype) => resolveHeroPlugin(createSkinPlugin(archetype))),
    (p) => p.archetype,
  ),
  ...BESPOKE,
};

const THEMES: Readonly<Record<string, HeroThemeTokens>> = Object.fromEntries(
  Object.entries(themeModules).map(([path, mod]) => [
    archetypeOfPath(path),
    Object.values(mod)[0] as HeroThemeTokens,
  ]),
);

/** Every archetype with a bespoke render plugin + theme (the registry-consistency guard reads this). */
export const REGISTERED_HERO_ARCHETYPES: readonly string[] = Object.keys(PLUGINS);

/** Resolve the render plugin for an archetype — the bespoke one, else the neutral procedural fallback. */
export function heroPluginFor(archetypeId: string | null): ResolvedHeroPlugin {
  const bespoke = archetypeId ? PLUGINS[archetypeId] : undefined;
  return bespoke ?? neutralPlugin(archetypeId ?? 'neutral');
}

/** The chosen hero's theme overrides, or null (no hero / a hero without a bespoke palette → base theme). */
export function heroThemeFor(archetypeId: string | null): HeroThemeTokens | null {
  return archetypeId ? (THEMES[archetypeId] ?? null) : null;
}
